import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getUsers,
  getUserById,
  createUser,
  updateUser,
  toggleUserActive,
  resetUserPassword,
  getRoles,
  getAuditLogs,
} from '@/api/adminApi';
import type { UserFilters, AuditLogFilters, CreateUserRequest, UpdateUserRequest } from '@/types/admin';

export function useUsers(acqFilters: UserFilters) {
  return useQuery({
    queryKey: ['admin', 'users', acqFilters],
    queryFn: () => getUsers(acqFilters),
    placeholderData: (acqPrevious) => acqPrevious,
  });
}

export function useUserById(acqId: string | undefined) {
  return useQuery({
    queryKey: ['admin', 'users', acqId],
    queryFn: () => getUserById(acqId!),
    enabled: !!acqId,
  });
}

export function useCreateUser() {
  const acqQueryClient = useQueryClient();
  return useMutation({
    mutationFn: (acqData: CreateUserRequest) => createUser(acqData),
    onSuccess: () => {
      acqQueryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
    },
  });
}

export function useUpdateUser() {
  const acqQueryClient = useQueryClient();
  return useMutation({
    mutationFn: (acqData: UpdateUserRequest) => updateUser(acqData),
    onSuccess: () => {
      acqQueryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
    },
  });
}

export function useToggleActive() {
  const acqQueryClient = useQueryClient();
  return useMutation({
    mutationFn: (acqId: string) => toggleUserActive(acqId),
    onSuccess: () => {
      acqQueryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
    },
  });
}

export function useResetPassword() {
  return useMutation({
    mutationFn: (acqId: string) => resetUserPassword(acqId),
  });
}

export function useRoles() {
  return useQuery({
    queryKey: ['admin', 'roles'],
    queryFn: getRoles,
    staleTime: 5 * 60 * 1000,
  });
}

export function useAuditLogs(acqFilters: AuditLogFilters) {
  return useQuery({
    queryKey: ['admin', 'audit-logs', acqFilters],
    queryFn: () => getAuditLogs(acqFilters),
    placeholderData: (acqPrevious) => acqPrevious,
  });
}
